// src/utils/knowYourNumbersPdf.js
// Draw Know Your Numbers values onto the coach's 2-page worksheet. The template
// has NO AcroForm fields, so this overlays text at measured positions
// (pdftotext -bbox-layout anchors, letter pages 612x792pt, verified by
// pdftoppm visual proof — see docs/superpowers/specs/2026-08-14 design).
// LAYOUT y is from the TOP of the page (like the extraction tool reports);
// drawText converts to pdf-lib's bottom-origin at render time.
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { downloadBytes } from "./impactScorecardPdf";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/know-your-numbers-template.pdf`;

const INK = rgb(0.13, 0.16, 0.23);
const WHITE = rgb(1, 1, 1);

// {page (0-based), x, y (top-based baseline), size, color?, maxWidth?}
export const LAYOUT = {
  name: { page: 0, x: 70, y: 164.5, size: 10, maxWidth: 310 },
  date: { page: 0, x: 437, y: 164.5, size: 10 },

  unitsYtd: { page: 0, x: 46, y: 233.5, size: 11 },
  volumeYtd: { page: 0, x: 227, y: 233.5, size: 11 },
  incomeYtd: { page: 0, x: 414, y: 233.5, size: 11 },

  avgSalesPrice: { page: 0, x: 90, y: 308, size: 9 },
  avgCommissionPct: { page: 0, x: 258, y: 308, size: 9 },
  perFileCosts: { page: 0, x: 329, y: 308, size: 8 },
  avgIncomePerClosing: { page: 0, x: 471, y: 308, size: 9 },

  leadsPerMonth: { page: 0, x: 46, y: 376.5, size: 11 },
  closingsPerMonth: { page: 0, x: 325, y: 376.5, size: 11 },
  closingRatio: { page: 0, x: 340, y: 400.5, size: 10 },

  hoursPerWeek: { page: 0, x: 46, y: 473.5, size: 11 },
  weeksWorkedYtd: { page: 0, x: 229, y: 473.5, size: 11 },
  totalHoursYtd: { page: 0, x: 413, y: 473.5, size: 11 },
  hourlyWage: { page: 0, x: 340, y: 500, size: 11, color: "white" },

  task1: { page: 0, x: 68, y: 609, size: 9, maxWidth: 280 },
  task1Hrs: { page: 0, x: 361, y: 609, size: 9 },
  task1Owner: { page: 0, x: 433, y: 609, size: 9, maxWidth: 145 },
  task2: { page: 0, x: 68, y: 633, size: 9, maxWidth: 280 },
  task2Hrs: { page: 0, x: 361, y: 633, size: 9 },
  task2Owner: { page: 0, x: 433, y: 633, size: 9, maxWidth: 145 },
  task3: { page: 0, x: 68, y: 657, size: 9, maxWidth: 280 },
  task3Hrs: { page: 0, x: 361, y: 657, size: 9 },
  task3Owner: { page: 0, x: 433, y: 657, size: 9, maxWidth: 145 },

  staffingNoneX: { page: 0, x: 239, y: 677, size: 10 },
  staffingDelegateX: { page: 0, x: 310, y: 677, size: 10 },
  staffingHireX: { page: 0, x: 451, y: 677, size: 10 },
  staffingAdjustment: { page: 0, x: 214, y: 693.5, size: 9, maxWidth: 170, wrap2: true },
  staffingBy: { page: 0, x: 450, y: 693.5, size: 9 },

  goalDate: { page: 1, x: 106, y: 98.5, size: 10 },
  monthsToGoal: { page: 1, x: 441, y: 98.5, size: 10 },

  todayIncome: { page: 1, x: 214, y: 143, size: 10 },
  goalIncome: { page: 1, x: 353, y: 143, size: 10 },
  gapIncome: { page: 1, x: 465, y: 143, size: 10 },
  todayVolume: { page: 1, x: 214, y: 166, size: 10 },
  goalVolume: { page: 1, x: 353, y: 166, size: 10 },
  gapVolume: { page: 1, x: 465, y: 166, size: 10 },
  todayClosings: { page: 1, x: 214, y: 188.5, size: 10 },
  goalClosings: { page: 1, x: 353, y: 188.5, size: 10 },
  gapClosings: { page: 1, x: 465, y: 188.5, size: 10 },
  todayLeads: { page: 1, x: 214, y: 211, size: 10 },
  goalLeads: { page: 1, x: 353, y: 211, size: 10 },
  gapLeadsMonthly: { page: 1, x: 465, y: 211, size: 10 },
  todayHours: { page: 1, x: 214, y: 233.5, size: 10 },
  goalHours: { page: 1, x: 353, y: 233.5, size: 10 },
  gapHoursPerWeek: { page: 1, x: 465, y: 233.5, size: 10 },
  todaySeats: { page: 1, x: 214, y: 256, size: 10 },
  goalSeats: { page: 1, x: 353, y: 256, size: 10 },
  gapTeamSeats: { page: 1, x: 465, y: 256, size: 10 },

  gapClosingsPerMonth: { page: 1, x: 436, y: 285, size: 10 },
  gapLeadsPerMonth: { page: 1, x: 410, y: 313, size: 10 },
  myGapClosings: { page: 1, x: 125, y: 348.5, size: 11, color: "white" },
  myGapLeads: { page: 1, x: 275, y: 348.5, size: 11, color: "white" },

  biggestGap1: { page: 1, x: 50, y: 408.5, size: 9, maxWidth: 525 },
  biggestGap2: { page: 1, x: 50, y: 433.5, size: 9, maxWidth: 525 },
  biggestGap3: { page: 1, x: 50, y: 458.5, size: 9, maxWidth: 525 },

  move1: { page: 1, x: 68, y: 535, size: 9, maxWidth: 345 },
  move1Weekly: { page: 1, x: 429, y: 535, size: 9 },
  move1Starts: { page: 1, x: 505, y: 535, size: 9 },
  move2: { page: 1, x: 68, y: 559, size: 9, maxWidth: 345 },
  move2Weekly: { page: 1, x: 429, y: 559, size: 9 },
  move2Starts: { page: 1, x: 505, y: 559, size: 9 },
  move3: { page: 1, x: 68, y: 583, size: 9, maxWidth: 345 },
  move3Weekly: { page: 1, x: 429, y: 583, size: 9 },
  move3Starts: { page: 1, x: 505, y: 583, size: 9 },

  sentenceLeads: { page: 1, x: 100, y: 641.5, size: 10 },
  sentenceDeals: { page: 1, x: 283, y: 641.5, size: 10 },
  sentenceIncome: { page: 1, x: 60, y: 661.5, size: 10 },
};

// Helvetica throws on characters outside WinAnsi (emoji etc.) — strip them.
const winAnsiSafe = (s) =>
  String(s == null ? "" : s).replace(/[^\x20-\x7E -ÿ]/g, "");

// Fill the template with the value map from buildKnowYourNumbersValues.
// templateBytes is optional (the browser fetches from public/); node proof
// scripts and tests pass the bytes in directly.
export async function fillKnowYourNumbersPdf(values, templateBytes) {
  let bytes = templateBytes;
  if (!bytes) {
    const res = await fetch(TEMPLATE_URL);
    if (!res.ok) throw new Error(`Know Your Numbers template not found (${res.status})`);
    bytes = await res.arrayBuffer();
  }
  const pdfDoc = await PDFDocument.load(bytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();

  // Greedy word split: as much as fits on line one, the rest on line two.
  const wrapTwo = (t, s, max) => {
    const words = t.split(/\s+/);
    let l1 = "";
    let i = 0;
    while (i < words.length) {
      const next = l1 ? `${l1} ${words[i]}` : words[i];
      if (l1 && font.widthOfTextAtSize(next, s) > max) break;
      l1 = next;
      i += 1;
    }
    return [l1, words.slice(i).join(" ")];
  };

  Object.entries(LAYOUT).forEach(([key, spot]) => {
    const raw = winAnsiSafe(values?.[key]);
    if (!raw) return;
    const page = pages[spot.page];
    if (!page) return;
    const draw = (t, yTop, s) =>
      page.drawText(t, {
        x: spot.x,
        y: page.getHeight() - yTop,
        size: s,
        font,
        color: spot.color === "white" ? WHITE : INK,
      });
    let text = raw;
    let size = spot.size;
    const overflows = (t, s) => font.widthOfTextAtSize(t, s) > spot.maxWidth;
    if (spot.maxWidth && overflows(text, size)) {
      if (spot.wrap2) {
        // A long answer wraps onto a second small line under the blank
        // instead of being crushed into unreadably small, truncated text.
        let s = Math.min(size, 8);
        let [l1, l2] = wrapTwo(text, s, spot.maxWidth);
        while (s > 7 && overflows(l2, s)) {
          s -= 0.5;
          [l1, l2] = wrapTwo(text, s, spot.maxWidth);
        }
        while (l2.length > 1 && overflows(l2, s)) {
          l2 = l2.slice(0, -1);
        }
        draw(l1, spot.y, s);
        if (l2) draw(l2, spot.y + s + 2, s);
        return;
      }
      // Other cells shrink to fit (down to 7pt), then truncate.
      while (size > 7 && overflows(text, size)) {
        size -= 0.5;
      }
      while (text.length > 1 && overflows(text, size)) {
        text = text.slice(0, -1);
      }
    }
    draw(text, spot.y, size);
  });

  return pdfDoc.save();
}

export { downloadBytes };
